import React, { useState } from 'react';
import { config } from '../config';

interface MerchantLogoProps {
    merchant: string;
    logo?: {
        hasLogo: boolean;
        location: 'bundled' | 'blob' | null;
        filename: string | null;
        fallbackColor: string;
    };
    size?: number;
}

/**
 * MerchantLogo component - displays merchant logo with fallback chain:
 * 1. Bundled logo (from frontend/public/merchant-logos/)
 * 2. Blob storage logo (from Azure Blob)
 * 3. Colored circle with first letter (fallback)
 */
const MerchantLogo: React.FC<MerchantLogoProps> = ({ merchant, logo, size = 44 }) => {
    const [imageError, setImageError] = useState(false);

    // If no logo metadata or image failed to load, show fallback
    if (!logo || !logo.hasLogo || imageError) {
        const initial = merchant.charAt(0).toUpperCase();
        const color = logo?.fallbackColor || '#667eea';

        return (
            <div
                style={{
                    width: `${size}px`,
                    height: `${size}px`,
                    borderRadius: '12px',
                    background: color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: `${size / 2.4}px`,
                    fontWeight: 600,
                    color: '#fff',
                    flexShrink: 0
                }}
            >
                {initial}
            </div>
        );
    }

    // Determine image URL based on location
    let imageUrl: string;
    if (logo.location === 'bundled') {
        // Bundled logos served from public folder
        imageUrl = `/merchant-logos/${logo.filename}`;
    } else if (logo.location === 'blob') {
        // Blob storage logos (public container)
        imageUrl = `${config.blobStorageUrl}/merchant-logos/${logo.filename}`;
    } else {
        // Shouldn't happen, but fallback
        return (
            <div
                style={{
                    width: `${size}px`,
                    height: `${size}px`,
                    borderRadius: '12px',
                    background: logo.fallbackColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: `${size / 2.4}px`,
                    fontWeight: 600,
                    color: '#fff',
                    flexShrink: 0
                }}
            >
                {merchant.charAt(0).toUpperCase()}
            </div>
        );
    }

    return (
        <div
            style={{
                width: `${size}px`,
                height: `${size}px`,
                borderRadius: '12px',
                overflow: 'hidden',
                flexShrink: 0,
                background: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '6px'
            }}
        >
            <img
                src={imageUrl}
                alt={`${merchant} logo`}
                style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                }}
                onError={() => setImageError(true)}
            />
        </div>
    );
};

export default MerchantLogo;
